import { Input, makeStyles, mergeClasses, Spinner, Textarea } from '@fluentui/react-components';
import React, { PropsWithChildren } from 'react';
import { customPalette } from '../../theme';

interface ITextFieldProps {
    id: string;
    className?: string;
    value: string;
    ariaLabel?: string;
    isProcessing?: boolean;
    isErrorInput?: boolean;
    processingLabel?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onEnter?: () => void;
    readOnly?: boolean;
    multiline?: boolean;
    resizable?: boolean;
    placeholder?: string;
    disabled?: boolean;
    selectAllOnFocus?: boolean;
    autoFocus?: boolean;
}

const useStyles = makeStyles({
    root: {
        paddingTop: '4px',
        paddingBottom: '4px',
        paddingLeft: '4px',
        paddingRight: '4px',
        outlineStyle: 'none',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
        boxShadow: '0px 2px 1px lightgray',
        borderBottomWidth: '2px',
        borderBottomStyle: 'solid',
        maxWidth: 'unset',
        width: '100%',
    },
    rootReadOnly: {
        boxShadow: 'none',
    },
    rootError: {
        borderTopColor: customPalette.red,
        borderRightColor: customPalette.red,
        borderBottomColor: customPalette.red,
        borderLeftColor: customPalette.red,
    },
    rootDefault: {
        borderTopColor: customPalette.greyD1D3D4,
        borderRightColor: customPalette.greyD1D3D4,
        borderBottomColor: customPalette.blue075A92,
        borderLeftColor: customPalette.greyD1D3D4,
    },
    spinner: {
        paddingRight: '5px',
        opacity: 0.5,
    },
    textarea: {
        width: '100%',
    },
});

const CustomTextField: React.FC<PropsWithChildren<ITextFieldProps>> = ({
    id,
    className,
    ariaLabel,
    isProcessing,
    isErrorInput,
    processingLabel,
    onChange,
    onBlur,
    onEnter,
    value,
    readOnly,
    multiline,
    resizable,
    placeholder,
    disabled,
    autoFocus = true,
    selectAllOnFocus = true,
}) => {
    const styles = useStyles();

    const rootClassName = mergeClasses(
        styles.root,
        readOnly && styles.rootReadOnly,
        isErrorInput ? styles.rootError : styles.rootDefault,
        multiline && styles.textarea,
        className
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, data: { value: string }) => {
        onChange?.(event, data.value);
    };

    const handleSelectAll = (ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (selectAllOnFocus) {
            ev.currentTarget.select();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && onEnter) {
            onEnter();
        }
    };

    const processingSpinner = isProcessing ? (
        <Spinner size='extra-tiny' aria-label={processingLabel} className={styles.spinner} />
    ) : undefined;

    if (multiline) {
        return (
            <Textarea
                id={id}
                className={rootClassName}
                value={value}
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                onChange={handleChange}
                onFocus={handleSelectAll}
                onBlur={onBlur}
                onKeyDown={handleKeyDown}
                readOnly={readOnly}
                resize={resizable ? 'vertical' : 'none'}
                placeholder={placeholder}
                disabled={disabled}
            />
        );
    }

    return (
        <Input
            id={id}
            className={rootClassName}
            value={value}
            aria-label={ariaLabel}
            autoFocus={autoFocus}
            onChange={handleChange}
            onFocus={handleSelectAll}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            contentAfter={processingSpinner}
            readOnly={readOnly}
            placeholder={placeholder}
            disabled={disabled}
        />
    );
};

export default CustomTextField;
